import axios from 'axios';

const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
  timeout: 60000,
});

const headshotCache = new Map();
const entriesCache = new Map();

const entriesCacheKey = (listId) => String(listId || '');

export const getPlayerHeadshot = (firstName, lastName, country, headshotUrl) => {
  if (headshotUrl) {
    return Promise.resolve(headshotUrl);
  }

  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (!name) return Promise.resolve(null);
  const normalizedCountry = String(country || '').trim().toUpperCase();
  const cacheKey = `${name.toLowerCase()}|${normalizedCountry}`;

  if (headshotCache.has(cacheKey)) {
    return headshotCache.get(cacheKey);
  }

  const request = publicApi
    .get('/api/public/headshot', { params: { name, country: normalizedCountry || undefined } })
    .then((res) => res.data?.url || null)
    .catch(() => null);

  headshotCache.set(cacheKey, request);
  return request;
};

export const getRankingReplacements = (listId) => {
  if (!listId) {
    return Promise.resolve({ replacements: [], reportDate: null, displayMode: 'none', message: '' });
  }

  return publicApi
    .get(`/api/public/rankings/${listId}/replacements`)
    .then((res) => ({
      replacements: res.data?.replacements || [],
      reportDate: res.data?.reportDate || null,
      displayMode: res.data?.displayMode || 'none',
      message: res.data?.message || '',
      sourceListName: res.data?.sourceListName || '',
    }))
    .catch(() => ({ replacements: [], reportDate: null, displayMode: 'none', message: '' }));
};

export const getRankingEntries = async (listId) => {
  if (!listId) return [];

  const cacheKey = entriesCacheKey(listId);
  if (entriesCache.has(cacheKey)) {
    return entriesCache.get(cacheKey);
  }

  const request = (async () => {
    const limit = 100;
    const firstPage = await publicApi.get(`/api/public/rankings/${listId}/entries`, {
      params: { page: 1, limit },
    });
    const entries = firstPage.data?.entries || [];
    const total = firstPage.data?.total || entries.length;
    const totalPages = Math.ceil(total / limit);

    if (totalPages <= 1) return entries;

    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        publicApi.get(`/api/public/rankings/${listId}/entries`, {
          params: { page: index + 2, limit },
        })
      )
    );

    return remainingPages.reduce(
      (allEntries, response) => allEntries.concat(response.data?.entries || []),
      entries
    );
  })();

  entriesCache.set(cacheKey, request);
  return request;
};

export default publicApi;
